import './StageForm.css'
import { memo, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { DragDropContext } from 'react-beautiful-dnd'
import SmoothCollapse from 'react-smooth-collapse'
import { useCallback } from 'react'

import { useFillStageMutation, useGetStageQuery } from '../../redux/stageService/stageApiSlice'
import { addLevel, addGroup, removeLevel, setStageLevels, setImmutableState } from '../../redux/stageService/stageSlice'
import { useRemoveStageMutation } from '../../redux/singleTournamentService/singleTournamentApiSlice'
import { dragParticipant } from '../../redux/stageService/stageSlice'
import { assignAlphabeticalNames, checkEquality } from '../../generalExternalFunctions'
import { Button } from '../../components/Button/Button'
import { StageGroup } from '../StageGroup/StageGroup'
import { ButtonWhite } from '../../components/ButtonWhite/ButtonWhite'
import { ReactComponent as ArrowDown } from '../../icon/arrow-down.svg'
import { ReactComponent as Bucket } from '../../icon/bucket.svg'
// import { ReactComponent as EditBlueIcon } from '../../icon/build_circle_blue.svg'
// import { ReactComponent as CloseIcon } from '../../icon/close.svg'

import {
	dragItems,
	formLevelsAndNodes,
	generateGroupName,
	definePlacedPlayers,
	formNodesForRequest,
	updateNodesList,
	existingMatches,
	checkFinishedMatches
} from './externalFunctions'

const StageFormComponent = ({stageNumber, tournamentRefetch, menuTab}) => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const { tournamentUid } = useParams()
	const stageLevels = useSelector(state => state.stage.stage.levels)
	const tournamentState = useSelector(state => state.tournament.tournament)
	const [fillStage] = useFillStageMutation()
	const [removeStage] = useRemoveStageMutation()
	const immutableLevelsState = useSelector(state => state.stage.immutableState)

	const { data: stageData = {}, error, isLoading, refetch } = useGetStageQuery({
		tournament_uid: tournamentUid,
		stageNumber: stageNumber
	}) // Stage data

	const [editingMode, setEditingMode] = useState(false)
	const [nodesList, setNodesList] = useState([])
	const [showParticipantsList, setShowParticipantsList] = useState(false)
	const [showSaveButton, setShowSaveButton] = useState(false)
	const [chosenPlayerInlist, setChosenPlayerInList] = useState({})
	const [updatePlayersPlaces, setUpdatePlayersPlaces] = useState(false)
	const [replacingItem, setReplacingItem] = useState({})
	const [dndDisabled, setDndDisabled] = useState(false)

	useEffect(() => {
		if (Object.keys(stageData).length !== 0) {
			const levelsWithNamedGroup = assignAlphabeticalNames(stageData)

			let formedLevelsAndNodes = null

			formedLevelsAndNodes = formLevelsAndNodes(
				levelsWithNamedGroup?.levels,
				tournamentState?.players || tournamentState?.doubles,
				tournamentState?.type,
				stageNumber
			)

			const filtredNodesList = formedLevelsAndNodes?.sortedNodes.filter(node => {
				return node?.bye !== true || node?.disabled !== true
			})

			setNodesList(filtredNodesList)
			dispatch(setStageLevels(formedLevelsAndNodes?.levelsWithformedGroupsNodes))
			dispatch(setImmutableState(formedLevelsAndNodes?.levelsWithformedGroupsNodes))

			setDndDisabled(existingMatches(stageData))

			if(stageNumber > 1){
				setDndDisabled(checkFinishedMatches(stageData))
			}
		}
	}, [stageData, stageNumber, tournamentState, dispatch])

	useEffect(() => {
		refetch()
	}, [refetch, stageNumber, menuTab])

	useEffect(() => {
		if (updatePlayersPlaces) {
			const placedPlayers = definePlacedPlayers(stageLevels, nodesList)
			setNodesList(placedPlayers)
			setUpdatePlayersPlaces(false)
		}

		const isEquale = checkEquality(immutableLevelsState, stageLevels, tournamentState?.type)

		if (isEquale) {
			setShowSaveButton(false)
		} else {
			const newNodesList = updateNodesList(stageLevels, nodesList)
			setNodesList(newNodesList)
			setShowSaveButton(true)
		}
	}, [stageLevels, immutableLevelsState, tournamentState?.type])

	const isUpdatePlayersPlaces = useCallback((data) => {
		setUpdatePlayersPlaces(data)
	}, [])

	const getPlayerStateFromChild = useCallback((data) => {
		setChosenPlayerInList(data)
	}, [])

	const onDragEnd = useCallback((result) => {
		setReplacingItem({})

		if (!result.destination) {
			return
		}

		const endLevelIndex = result.destination.droppableId.split('-')[0]
		const finishGroupIndex = result.destination.droppableId.split('-')[1]
		const replacedNode = {
			...stageLevels[endLevelIndex]?.groups[finishGroupIndex]?.nodes[result.destination.index]
		}
		const finishGroup = { ...stageLevels[endLevelIndex]?.groups[finishGroupIndex] }

		if (replacedNode?.disabled || finishGroup?.nodes?.length - 1 < result.destination.index) {
			return
		}

		const obj = dragItems(result, stageLevels)
		dispatch(dragParticipant({ obj }))
	}, [stageLevels, dispatch])

	const handleParticipantsList = useCallback(() => {
		setShowParticipantsList(prev => !prev)
	}, [])

	// const handlePlayerInParticipantsList = useCallback((n, index) => {

	// 	setChosenPlayerInList({ player: n, index: index })
	// }, [])

	const handleAddGroup = useCallback((levelIndex) => {
		setChosenPlayerInList({})
		const groupName = generateGroupName(stageLevels)

		dispatch(addGroup({
			levelIndex,
			group: {
				'order_number': stageLevels?.[levelIndex]?.groups?.length + 1,
				'name': groupName,
				'type': 'round_robin',
				'participants_count': 0,
				'incoming_nodes_count': 0,
				'outgoing_nodes_count': 0,
				'nodes': [],
				'matches': []
			}
		}))
	}, [stageLevels, dispatch])

	const handleAddLevel = useCallback(() => {
		setChosenPlayerInList({})
		const groupName = generateGroupName(stageLevels)

		dispatch(addLevel(
			{
				'order_number': stageLevels?.length + 1,
				'name': null,
				'groups': [
					{
						'order_number': 1,
						'name': groupName,
						'type': 'round_robin',
						'participants_count': 0,
						'incoming_nodes_count': 0,
						'outgoing_nodes_count': 0,
						'nodes': [],
						'matches': []
					}
				]
			}
		))
	}, [])

	const handleRemoveLevel = useCallback((levelIndex) => {
		setChosenPlayerInList({})
		const newLevels = stageLevels?.filter((lvl, index) => index !== levelIndex)
		let sortedLevels = newLevels?.sort((a, b) => a.order_number - b.order_number)
			.map((group, index) => ({ ...group, order_number: index + 1 }))

		if (sortedLevels < 1) {
			sortedLevels = [{
				'order_number': 1,
				'name': null,
				'groups': [
					{
						'order_number': 1,
						'name': 'A',
						'type': 'round_robin',
						'participants_count': 0,
						'incoming_nodes_count': 0,
						'outgoing_nodes_count': 0,
						'nodes': [],
						'matches': []
					}
				]
			}]
		}

		dispatch(removeLevel(sortedLevels))
	}, [stageLevels, dispatch])

	const handleRemoveStage = useCallback(async () => {
		try {
			let response = {}

			for (let i = stageNumber - 1; i < tournamentState?.stages_count; i++) {
				response = await removeStage({
					tournament_uid: tournamentUid
				}).unwrap()

				if (response?.error) {
					console.log('Remove stage return error', response?.error)
				}
			}

			tournamentRefetch()

			setEditingMode(false)
			const stageTabNumber = response?.order_number > 1 ? response?.order_number - 1 : 1
			sessionStorage.setItem('stageOrder', stageTabNumber)

			setNodesList([])
			setShowParticipantsList(false)
			setShowSaveButton(false)
			setChosenPlayerInList({})
			setUpdatePlayersPlaces(false)
			setReplacingItem({})
		} catch (e) {
			console.log('handleRemoveStage stage catch error', e)
		}
	}, [tournamentUid, stageNumber, tournamentState, removeStage])

	const handleSaveFormedStage = useCallback(() => {
		try {
			const readyNodes = formNodesForRequest(stageData, stageLevels, tournamentState?.type)
			const response = fillStage({
				tournament_uid: tournamentUid,
				stageNumber: stageNumber,
				body: readyNodes
			}).unwrap()

			if (response?.error) {
				console.log('formStage return error', response?.error)
			}
		} catch (e) {
			console.log('handleSaveFormedStage catch error', e)
		}
	}, [stageData, stageLevels, tournamentState?.type, stageNumber, tournamentUid, fillStage])

	if (error) { console.log(error) }
	if (isLoading) { return <h1>Loading</h1> }

	return (
		<>
			<div className="stage-form__wrapper">
				<div
					className="stage-form__container"
					style={{
						paddingBottom: showSaveButton ?
							'160px'
							:
							'100px'
					}}
				>
					{/* <div className="stage-form__button-container">
						{
							editingMode ?
								<CloseIcon
									className="stage-form__button-edition-mode"
									onClick={() => setEditingMode(false)} 
								/>
								:
								<EditBlueIcon
									className="stage-form__button-edition-mode"
									onClick={() => setEditingMode(true)}
								/>
						}
					</div> */}

					<div className="stage-form__participants-list">
						<div className="stage-form__participants-list-header" onClick={handleParticipantsList}>
							<p className="stage-form__participants-list-title">
								{t('Participants')} ({nodesList?.length})
							</p>

							<ArrowDown className={showParticipantsList ? 'arrow--up' : 'arrow--down'} />
						</div>

						<SmoothCollapse expanded={showParticipantsList}>
							<div className="stage-form__participants-list-container">
								{
									nodesList?.map((n, index) =>
										<div
											className={index === chosenPlayerInlist.index ?
												'stage-form__participants-list-player--chosen'
												:
												'stage-form__participants-list-player'
											}
											key={index}
											style={{
												backgroundColor: n?.placed ? '#EBF4FF' : 'transparent',
												padding: tournamentState?.type === 'singles' ? '10px' : '6px'
											}}
										>
											{
												tournamentState?.type === 'singles' &&
												<>
													{
														n?.playerUid ?
															<p
															// onClick={() => handlePlayerInParticipantsList(n, index)}
															>
																{`(${n?.rating}) ${n?.name}`}
															</p>
															:
															<p
															// onClick={() => handlePlayerInParticipantsList(n, index)}
															>
																{`${n?.name}`}
															</p>
													}
												</>
											}

											{
												tournamentState?.type === 'doubles' && !n?.noPlayer &&
												<div
													key={index}
													className={index === chosenPlayerInlist.index ?
														'stage-form__participants-list-doubles--chosen'
														:
														'stage-form__participants-list-doubles'
													}
													// onClick={() => handlePlayerInParticipantsList(n, index)}
													style={{
														backgroundColor: n?.placed ?
															'#EBF4FF'
															:
															'transparent'
													}}
												>
													<p className="stage-form__participants-list-doubles-raiting">
														({n?.rating})
													</p>

													<div>
														<p className="stage-form__participants-list-doubles-name">
															{n?.firstPlayerName}
														</p>

														<p className="stage-form__participants-list-doubles-name">
															{n?.secondPlayerName}
														</p>
													</div>
												</div>
											}
										</div>
									)
								}
							</div>
						</SmoothCollapse>
					</div>

					<DragDropContext
						onDragEnd={(result) => onDragEnd(result)}
						onDragUpdate={(result) => setReplacingItem(result?.destination)}
					>
						{
							stageLevels?.map((level, levelIndex) =>
								<div key={levelIndex} className="stage-form__level">
									{
										stageLevels?.length > 1 &&
										<div className="stage-form__level-header">
											<p className="stage-form__level-name">
												{t('Level')} {level?.order_number}
											</p>

											{
												editingMode &&
												<Bucket
													className="delete-icon"
													onClick={() => handleRemoveLevel(levelIndex)}
												/>
											}
										</div>
									}

									<div className="stage-form__group-container">
										{
											level?.groups?.map((group, groupIndex) =>
												<div key={groupIndex} className="stage-form__group">
													<StageGroup
														group={group}
														levelIndex={levelIndex}
														groupIndex={groupIndex}
														tournament={tournamentState}
														editingMode={editingMode}
														nodesList={nodesList}
														levels={stageLevels}
														sendPlayerState={getPlayerStateFromChild}
														updatePlaces={isUpdatePlayersPlaces}
														chosenPlayerInlist={chosenPlayerInlist}
														replacingItem={replacingItem}
														dndDisabled={dndDisabled}
													/>
												</div>
											)
										}
									</div>

									{
										editingMode &&
										<ButtonWhite
											title={t('Add group')}
											onClick={() => handleAddGroup(levelIndex)}
										/>
									}
								</div>
							)
						}
					</DragDropContext>

					{
						editingMode &&
						<div className="stage-form__button-level-addition">
							<ButtonWhite
								title={t('Add level')}
								onClick={() => handleAddLevel()}
							/>
						</div>
					}

					{
						showSaveButton &&
						<div className="stage-form__button">
							<Button
								onClick={() => handleSaveFormedStage()}
								title={t('Save')}
							/>
						</div>
					}

					{
						<div className="stage-form__container-deletion">
							<button
								onClick={handleRemoveStage}
								className="stage-form__button-deletion"
								type="button"
							>
								{t('Remove stage')}
							</button>
						</div>
					}
				</div>
			</div>
		</>
	)
}

export const StageForm = memo(StageFormComponent)